import { LocalizedText, useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/LanguageContext";
import useNavigationTranslations from "../i18n/useNavigationTranslations";
import RemainingMissionsButton from "../missions/RemainingMissionsButton";
import {
  buildGuestLoginDestination,
  isCentralMissionsPath,
} from "../missions/rubricRegistry";
import WalletControl from "../wallet/WalletControl";
import GlobalSearchBox from "../global-search/GlobalSearchBox";
import { useOptionalFeatureRegistry } from "../feature-status/FeatureRegistryContext";
import { isFeatureNavigable } from "../feature-status/registry";
import { getTodayCatalog } from "../feature-status/todayCatalog";
import NotificationBellPopover from "../notifications/NotificationBellPopover";
import { useNotificationCenterFeature } from "../notifications/NotificationFeatureGate";
import FriendsDock from "./FriendsDock";
import MemberAvatar from "./MemberAvatar";
import EternuaIcon, { iconForPath } from "./EternuaIcon";
import CollapsibleQuickNavigation from "./CollapsibleQuickNavigation";
import "./EternuaGlobalNav.css";
import "./CollapsibleQuickNavigation.css";
import lia3024Promotion from "../lia3024/promotionContent.json";

const ACCOUNT_LINKS = [
  { label: "Mon tableau de bord Premium", to: "/premium/tableau-de-bord", featureKey: "today-subscriptions" },
  { label: "Premium", labelKey: "subscriptions.nav.public", to: "/premium", aliases: ["/abonnements", "/mon-abonnement"], featureKey: "today-subscriptions" },
];

const DOCK_LINKS = [
  { icon: "◉", label: "Aujourd’hui", to: "/aujourdhui" },
  { icon: "▤", label: "Forum", to: "/forum", featureKey: "today-forum" },
  { icon: "⌑", label: "Marketplace", to: "/market", featureKey: "today-buy" },
  { icon: "♙", label: "Amis", to: "/amis", exact: true },
];

const MORE_PROJECT_LINKS = [
  { icon: "⌑", label: "Marketplace", to: "/market", featureKey: "today-buy" },
  { icon: "✦", label: "Mission Eternua", to: "/mission-eternua" },
  { icon: "◉", label: "Eternua aujourd’hui", to: "/aujourdhui" },
  { icon: "∞", label: "Vision 3024", to: "/vision-3024" },
  { icon: "♡", label: "Soutenir", to: "/soutenir-eternua" },
  { icon: "▶", label: "Direct LIA / Aelith", to: "/market/direct" },
  { icon: "✧", label: "44 IA humaines", to: "/ia-humaines" },
];

const CATEGORY_LINKS = [
  { icon: "🎁", label: "Donner", to: "/market/donner?tab=donner", panel: "donner" },
  { icon: "⌖", label: "Carte vivante", labelKey: "live_map.nav", to: "/carte-vivante", featureKey: "today-live-map" },
  { icon: "▣", label: "Emploi", to: "/market/emploi?tab=offres", panel: "emploi", featureKey: "today-employment" },
  { icon: "◇", label: "Vendre", to: "/mes-eternua/vendre/classique", panel: "vendre", featureKey: "today-sell" },
  { icon: "⌑", label: "Acheter", to: "/market/acheter?tab=parcourir", panel: "acheter", featureKey: "today-buy" },
  { icon: "⚒", label: "Enchères", to: "/market/encheres?tab=parcourir", panel: "encheres", featureKey: "today-auctions" },
  { icon: "ϟ", label: "Ventes flash", to: "/market/flash?tab=parcourir", featureKey: "today-flash-sales" },
  { icon: "⚙", label: "Services", to: "/market/services?tab=explorer", panel: "services", featureKey: "today-services" },
  { icon: "▥", label: "Sortie", to: "/market/sortie?tab=events", panel: "sortie", featureKey: "today-outings" },
  { icon: "➤", label: "Voyage", to: "/market/voyage", panel: "voyage", featureKey: "today-travel" },
  { icon: "◒", label: "Sport & loisirs", to: "/market/sport-loisir?tab=sport", panel: "sportloisir", featureKey: "today-sport" },
  { icon: "♧", label: "Animaux", to: "/market/animaux", panel: "animaux", featureKey: "today-animals" },
  { icon: "✿", label: "Babysitting", to: "/market/babysitting", panel: "babysitting", featureKey: "today-babysitting" },
  { icon: "⌂", label: "Immobilier & Séjours", labelKey: "immobilier.nav.label", to: "/market/immobilier?tab=sejours", featureKey: "today-real-estate" },
  { icon: "∞", label: "Crowdfunding", to: "/crowdfunding", featureKey: "today-crowdfunding" },
];

const MAIN_NAVIGATION = [
  { label: "Accueil", to: "/", exact: true },
  { label: lia3024Promotion.menu, labelKey: "lia.menu", to: lia3024Promotion.href, document: true },
  ...["Acheter", "Vendre", "Donner", "Services"].map((label) => CATEGORY_LINKS.find((item) => item.label === label)),
  { label: "Récompenses", to: "/mes-eternua" },
];

// Each Today service belongs to one directory section. Unknown catalogue
// entries remain discoverable in the final section instead of disappearing.
const TODAY_MENU_SECTIONS = [
  { label: "Achats & ventes", icon: "cart", keys: ["today-buy", "today-sell", "today-auctions", "today-flash-sales", "marketplace-contact-followup", "marketplace-secure-payment", "monetization-boosts", "monetization-commissions"] },
  { label: "Travail & services", icon: "briefcase", keys: ["today-employment", "employment-internal-application", "today-services", "services-quotes-followup", "services-payment"] },
  { label: "Sorties, voyages & loisirs", icon: "compass", keys: ["today-outings", "today-travel", "today-sport", "today-weather", "today-live-map"] },
  { label: "Famille, animaux & logement", icon: "home", keys: ["today-babysitting", "today-animals", "today-real-estate"] },
  { label: "Dons, entraide & projets", icon: "give", keys: ["today-donations", "today-mutual-aid", "today-crowdfunding"] },
  { label: "Organisation & protection", icon: "trust", keys: ["today-agenda", "today-guided-journeys", "today-document-vault", "today-protection", "notification-center", "today-global-search", "deterministic-filter-matching"] },
  { label: "Premium & espaces personnels", icon: "spark", keys: ["today-subscriptions", "premium-production", "today-wallet", "today-studio", "infini-infini"] },
  { label: "Autres découvertes", icon: "compass", keys: [] },
];

function groupTodayMenu(features) {
  const groups = TODAY_MENU_SECTIONS.map((section) => ({ ...section, features: [] }));
  features.forEach((feature) => {
    const group = groups.find((section) => section.keys.includes(feature.featureKey)) || groups[groups.length - 1];
    group.features.push(feature);
  });
  return groups.filter((section) => section.features.length > 0);
}

const SOCIAL_LINKS = [
  { icon: "◌", label: "Communauté", to: "/communaute", aliases: ["/groupes", "/cercles", "/evenements", "/projets"], featureKey: "today-community" },
  { icon: "▤", label: "Actu", to: "/amis/actu", exact: true },
  { icon: "♫", label: "Musique & clips", accessibleLabel: "Musique & Clips", to: "/music", exact: true },
  { icon: "⌘", label: "Jeux", to: "/jeux", exact: true },
  { icon: "♙", label: "Amis", to: "/amis", exact: true },
  { icon: "♧", label: "Demandes", to: "/amis#demandes", exact: true, hash: "#demandes" },
  { icon: "◎", label: "Cercles", accessibleLabel: "Cercles du jour", labelKey: "daily_circles.nav", to: "/cercles-du-jour" },
  {
    icon: "⌕",
    label: "Appels",
    to: "/live",
    aliases: [
      "/video",
      "/temple-lia-video",
      "/temple-ternity-video",
      "/chat",
    ],
  },
  { icon: "◎", label: "Salon amis", to: "/amis/salon" },
  { icon: "◉", label: "Salons groupe", accessibleLabel: "Salons de groupe historiques", to: "/amis/groupes", exact: true },
];

const MARKET_CART_STORAGE_KEY = "eternua.market.buy.cart.v2";

// The two Plus buttons have distinct contents. Social destinations from the
// Today catalogue belong to the bottom menu as well, never to both menus.
const SOCIAL_PATHS = new Set([
  ...SOCIAL_LINKS.flatMap((item) => [item.to, ...(item.aliases || [])]),
  "/mes-eternua",
].map((route) => route.split(/[?#]/)[0]));
const SOCIAL_FEATURE_KEYS = new Set([
  "today-community", "today-friends", "today-chat", "today-daily-circles",
]);
const isSocialFeature = (feature) => SOCIAL_FEATURE_KEYS.has(feature.featureKey)
  || SOCIAL_PATHS.has(stripDecorations(feature.route));

function readMarketCartCount() {
  if (typeof window === "undefined") return 0;
  try {
    const stored = JSON.parse(window.localStorage.getItem(MARKET_CART_STORAGE_KEY) || "[]");
    return Array.isArray(stored) ? stored.length : 0;
  } catch (_) {
    return 0;
  }
}

function stripDecorations(path) {
  return String(path || "").split(/[?#]/)[0];
}

function normalizeUnreadCount(value) {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;
}

function isLinkActive(location, item) {
  const path = stripDecorations(location?.pathname);
  const target = stripDecorations(item.to);
  if (item.exact) {
    if (path !== target) return false;
    if (item.hash) return location?.hash === item.hash;
    return !location?.hash;
  }
  if (path === target || path.startsWith(`${target}/`)) return true;
  return (item.aliases || []).some((alias) => path === alias || path.startsWith(`${alias}/`) || path.startsWith(`${alias}-`));
}

function SmartShortcut({ "data-et-copy": __etCopy = "", tone, icon, label, subtitle, to, current }) {
  useSourceLanguage();
  const iconPaths = {
    today: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5" /></>,
    vision: <><path d="m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5L12 3Z" /><path d="M20 2v4m-2-2h4" /></>,
    agenda: <><rect x="4" y="5" width="16" height="16" rx="3" /><path d="M8 3v4m8-4v4M4 10h16m-12 5h3m3 0h2m-8 3h3" /></>,
    journey: <><circle cx="6" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="M6 8.5V16a4 4 0 0 0 4 4h2M18 15.5V8a4 4 0 0 0-4-4h-2" /></>,
    security: <><path d="m12 3 8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3Z" /><path d="m8 12 2.5 2.5L16 9" /></>,
    studio: <><rect x="4" y="5" width="16" height="15" rx="4" /><path d="M12 2v3M8 11h.01M16 11h.01M8 15c2 2 6 2 8 0" /></>,
  };
  return (
    <Link
      className={`et-global-context-shortcut et-global-context-shortcut--${tone}`}
      to={to}
      aria-current={current ? "page" : undefined}
      aria-label={`${label} — ${subtitle}`}
    >
      <span className="et-global-context-shortcut__icon" aria-hidden="true">
        {iconPaths[tone] ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" focusable="false">{iconPaths[tone]}</svg> : icon}
      </span>
      <span className="et-global-context-shortcut__copy">
        <strong>{<LocalizedText source={label} marked={__etCopy.split(',').includes("label")} />}</strong>
        <small>{<LocalizedText source={subtitle} marked={__etCopy.split(',').includes("subtitle")} />}</small>
      </span>
      <span className="et-global-context-shortcut__arrow" aria-hidden="true">
        →
      </span>
    </Link>
  );
}

export default function EternuaGlobalNav({
  onPrimaryNavigate,
  onCategoryNavigate,
  paymentStatus,
  quickNavigation = { available: false },
}) {
  useSourceLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, forumUnreadCount = 0, chatUnreadCount = 0 } = useAuth();
  const { lang, setLang, langs } = useI18n();
  const { translate, translateLabel, translateMessage } = useNavigationTranslations();
  const itemLabel = (item) => item.labelKey
    ? translate(item.labelKey, item.label)
    : translateLabel(item.label);
  const featureRegistry = useOptionalFeatureRegistry();
  const { enabled: notificationCenterEnabled } = useNotificationCenterFeature();
  const getFeature = featureRegistry?.getFeature || (() => null);
  const registryReady = featureRegistry?.ready === true;
  const todayCatalog = getTodayCatalog(registryReady ? featureRegistry?.today : []);
  const todayFeatures = [
    ...todayCatalog.available, ...todayCatalog.readOnly,
    ...todayCatalog.privateBeta, ...todayCatalog.upcoming,
  ];
  const topTodayFeatures = todayFeatures.filter((feature) => !isSocialFeature(feature));
  const socialTodayFeatures = todayFeatures.filter(isSocialFeature);
  const absentFromCatalog = (features) => (item) => !features.some((feature) => (
    feature.featureKey === item.featureKey
    || (feature.route && stripDecorations(feature.route) === stripDecorations(item.to))
  ));
  const registryItemVisible = (item) => {
    if (!item.featureKey) return true;
    if (!registryReady) return false;
    const feature = getFeature(item.featureKey);
    if (
      item.featureKey === "today-community"
      && feature?.functionalStatus === "PRIVATE_BETA"
      && !(user?.is_staff || user?.is_superuser)
    ) {
      return false;
    }
    return isFeatureNavigable(feature);
  };
  const [moreOpen, setMoreOpen] = useState(false);
  const [compactMenu, setCompactMenu] = useState(() => Boolean(window.matchMedia?.("(max-width: 768px)")?.matches));
  useEffect(() => {
    const media = window.matchMedia?.("(max-width: 768px)");
    if (!media) return undefined;
    const update = () => setCompactMenu(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);
  const [searchFocused, setSearchFocused] = useState(false);
  const moreRef = useRef(null);
  const topMoreRef = useRef(null);
  const bottomMoreRef = useRef(null);
  const moreTriggerRef = useRef(null);
  const [secondaryNavHidden, setSecondaryNavHidden] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [messengerOpen, setMessengerOpen] = useState(false);
  const [messengerExpanded, setMessengerExpanded] = useState(false);
  const [marketCartCount, setMarketCartCount] = useState(readMarketCartCount);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const profileMenuRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const scrollDirectionRef = useRef(0);
  const scrollDistanceRef = useRef(0);
  const scrollFrameRef = useRef(null);
  const missionsShortcutHidden = isCentralMissionsPath(location.pathname || "");
  const isAgendaRoute = location.pathname.startsWith("/mes-eternua/agenda");
  const isTempleHomepage = location.pathname === "/";
  const emailVerificationRequired = Boolean(user) && (
    user?.access?.email_verified === false
    || user?.access?.account_status === "pending_email_verification"
  );
  const emailVerificationDestination = useMemo(() => {
    const currentDestination = `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`;
    const query = new URLSearchParams({ next: currentDestination });
    return `/verify-email?${query.toString()}`;
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const openMessenger = () => {
      if (!user) return;
      setMessengerOpen(true);
    };
    window.addEventListener("eternua:open-messenger", openMessenger);
    return () => window.removeEventListener("eternua:open-messenger", openMessenger);
  }, [user]);

  useEffect(() => {
    document.documentElement.classList.add("eternua-shell-v2-active");
    document.body.classList.add("eternua-shell-v2-active");
    document.documentElement.classList.add("eternua-global-nav-unified", "eternua-header-two-bars");
    document.body.classList.add("eternua-global-nav-unified", "eternua-header-two-bars");
    return () => {
      document.documentElement.classList.remove("eternua-shell-v2-active");
      document.body.classList.remove("eternua-shell-v2-active");
      document.documentElement.classList.remove("eternua-global-nav-unified", "eternua-header-two-bars");
      document.body.classList.remove("eternua-global-nav-unified", "eternua-header-two-bars");
    };
  }, []);

  useEffect(() => {
    setMessengerOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const syncMarketCart = (event) => {
      if (event?.type === "storage" && event.key !== MARKET_CART_STORAGE_KEY) return;
      const announcedCount = Number(event?.detail?.count);
      setMarketCartCount(
        Number.isFinite(announcedCount) ? Math.max(0, Math.trunc(announcedCount)) : readMarketCartCount()
      );
    };
    window.addEventListener("storage", syncMarketCart);
    window.addEventListener("eternua:market-cart-updated", syncMarketCart);
    return () => {
      window.removeEventListener("storage", syncMarketCart);
      window.removeEventListener("eternua:market-cart-updated", syncMarketCart);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search || "");
    if (params.get("messenger") !== "1") return;
    const username = String(params.get("contact") || "").trim();
    setMessengerExpanded(params.get("view") === "full");
    setMessengerOpen(true);
    if (username) {
      window.dispatchEvent(new CustomEvent("eternua:open-messenger", {
        detail: { username },
      }));
    }
  }, [location.search, user]);

  useEffect(() => {
    const hiddenClass = "eternua-global-nav-secondary-hidden";
    document.documentElement.classList.toggle(hiddenClass, secondaryNavHidden);
    document.body.classList.toggle(hiddenClass, secondaryNavHidden);
    return () => {
      document.documentElement.classList.remove(hiddenClass);
      document.body.classList.remove(hiddenClass);
    };
  }, [secondaryNavHidden]);

  useEffect(() => {
    const requiredClass = "eternua-email-verification-required";
    document.documentElement.classList.toggle(requiredClass, emailVerificationRequired);
    document.body.classList.toggle(requiredClass, emailVerificationRequired);
    return () => {
      document.documentElement.classList.remove(requiredClass);
      document.body.classList.remove(requiredClass);
    };
  }, [emailVerificationRequired]);

  useEffect(() => {
    if (moreOpen || searchFocused || profileMenuOpen || isAgendaRoute) {
      setSecondaryNavHidden(false);
      return undefined;
    }

    const getScrollY = () => Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0);
    const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
    const resetScrollDirection = () => {
      scrollDirectionRef.current = 0;
      scrollDistanceRef.current = 0;
    };
    const updateSecondaryNav = () => {
      const scrollY = getScrollY();
      const delta = scrollY - lastScrollYRef.current;
      lastScrollYRef.current = scrollY;

      if (scrollY <= 8) {
        resetScrollDirection();
        setSecondaryNavHidden(false);
        return;
      }

      if (Math.abs(delta) < 0.5) return;

      const direction = delta > 0 ? 1 : -1;
      if (direction === scrollDirectionRef.current) {
        scrollDistanceRef.current += Math.abs(delta);
      } else {
        scrollDirectionRef.current = direction;
        scrollDistanceRef.current = Math.abs(delta);
      }

      if (scrollDistanceRef.current < 12) return;

      const focusedElement = document.activeElement;
      const keyboardFocusInSecondaryNav =
        focusedElement instanceof HTMLElement &&
        focusedElement.matches(":focus-visible") &&
        focusedElement.closest(".et-header-navigation, .et-global-quick-nav");

      if (keyboardFocusInSecondaryNav) return;

      setSecondaryNavHidden(direction > 0);
      scrollDistanceRef.current = 0;
    };
    const handleScroll = () => {
      if (scrollFrameRef.current !== null) return;
      scrollFrameRef.current = requestFrame(() => {
        scrollFrameRef.current = null;
        updateSecondaryNav();
      });
    };

    lastScrollYRef.current = getScrollY();
    resetScrollDirection();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollFrameRef.current !== null) {
        cancelFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }
    };
  }, [isAgendaRoute, moreOpen, searchFocused, profileMenuOpen]);

  useEffect(() => {
    setSecondaryNavHidden(false);
    setProfileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;

    const closeProfileMenu = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };
    const closeProfileMenuWithKeyboard = (event) => {
      if (event.key === "Escape") {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeProfileMenu);
    document.addEventListener("keydown", closeProfileMenuWithKeyboard);
    return () => {
      document.removeEventListener("pointerdown", closeProfileMenu);
      document.removeEventListener("keydown", closeProfileMenuWithKeyboard);
    };
  }, [profileMenuOpen]);

  useEffect(() => {
    setMoreOpen(false);
    setSearchFocused(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    moreRef.current?.querySelector("button")?.focus();
    const closeOutside = (event) => {
      if (![moreRef, topMoreRef, bottomMoreRef].some((ref) => ref.current?.contains(event.target))) {
        setMoreOpen(false);
      }
    };
    const closeWithEscape = (event) => {
      if (event.key !== "Escape") return;
      setMoreOpen(false);
      moreTriggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, [moreOpen]);

  const toggleMore = (event, origin) => {
    moreTriggerRef.current = event.currentTarget;
    setProfileMenuOpen(false);
    setSearchFocused(false);
    setMoreOpen((current) => current === origin ? false : origin);
  };

  const renderMoreLinks = (links) => links.filter(registryItemVisible).map((item) => (
    <Link key={item.to} to={item.to} aria-label={translateLabel(item.accessibleLabel)}
      aria-current={isLinkActive(location, item) ? "page" : undefined}
      onClick={() => { setMoreOpen(false); onPrimaryNavigate?.(); }}>
      <EternuaIcon name={iconForPath(item.to)} />
      <strong>{itemLabel(item)}</strong>
    </Link>
  ));

  const renderTodayLinks = (features) => features.map((feature) => {
    const contents = <>
      <EternuaIcon name={iconForPath(feature.route || "")} />
      <span className="et-global-extra-links__copy">
        <strong>{translate(`navigation.catalog.${feature.featureKey}`, translateLabel(feature.title))}</strong>
        <small>{feature.premiumSpace ? (feature.premiumSpace.available ? translateLabel("Premium · Ouvrir mon espace") : translateLabel("Premium · En préparation")) : translate(`navigation.status.${feature.functionalStatus}`, feature.functionalStatusLabel)}</small>
      </span>
    </>;
    const destination = feature.premiumSpace?.route || feature.route;
    const canOpen = feature.premiumSpace?.route || (feature.route && registryItemVisible(feature));
    return canOpen ? (
      <Link key={feature.featureKey} to={destination}
        aria-current={isLinkActive(location, { to: destination }) ? "page" : undefined}
        onClick={() => { setMoreOpen(false); onPrimaryNavigate?.(); }}>
        {contents}
      </Link>
    ) : (
      <div key={feature.featureKey} className="et-global-extra-links__unavailable">
        {contents}
      </div>
    );
  });

  const displayName = useMemo(
    () => user?.public_profile?.display_name || user?.display_name || user?.first_name || user?.username || "Eternua",
    [user]
  );
  const unreadMessageCount = normalizeUnreadCount(chatUnreadCount);
  const notificationCount =
    normalizeUnreadCount(forumUnreadCount) +
    unreadMessageCount;
  const agendaDestination = user
    ? "/mes-eternua/agenda"
    : buildGuestLoginDestination("/mes-eternua/agenda");
  const protectionDestination = user
    ? "/mes-eternua/protection"
    : buildGuestLoginDestination("/mes-eternua/protection");
  const journeyDestination = user
    ? "/mon-parcours"
    : buildGuestLoginDestination("/mon-parcours");
  const studioDestination = user
    ? "/studio-lia"
    : buildGuestLoginDestination("/studio-lia");
  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
      setProfileMenuOpen(false);
      navigate("/login", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const openDockDestination = (event, destination) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    onPrimaryNavigate?.();
    navigate(destination);
  };

  return (
    <>
      <header
        className={`et-global-nav et-global-nav--two-bars${secondaryNavHidden ? " is-secondary-hidden" : ""}`}
      >
        <div className="et-global-nav__top">
          <Link className="et-global-brand" to="/" onClick={onPrimaryNavigate} aria-label={translateLabel("Accueil Eternua")}>
            <span className="et-global-orb" aria-hidden="true"><EternuaIcon name="infinity" /></span>
            <span className="et-global-brand__copy">
              <strong><LocalizedText source={"ETERNUA"} /></strong>
              <small>{translateLabel("La monnaie de l’infini")}</small>
              <span
                className={`et-global-payment-status et-global-payment-status--${paymentStatus?.state || "prelaunch"}`}
                title={translateLabel("État du service de paiement")}
              >
                {translateLabel(paymentStatus?.navigationLabel || "Paiements bêta")}
              </span>
            </span>
          </Link>

          <div className="et-header-inline-search"
            onFocusCapture={() => { setSearchFocused(true); setMoreOpen(false); }}
            onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setSearchFocused(false); }}>
            <GlobalSearchBox onNavigate={onPrimaryNavigate} />
          </div>
          <div className="et-global-actions" role="group" aria-label={translateLabel("Actions rapides")}>
            <Link className="et-header-cart" to="/market/panier" onClick={onPrimaryNavigate}
              aria-label={marketCartCount ? translateMessage("navigation.cart_count", `Ouvrir mon panier, ${marketCartCount} articles`, { count: marketCartCount }) : translateLabel("Ouvrir mon panier, panier vide")}>
              <EternuaIcon name="cart" /><span>{translateLabel("Panier")}</span>
              {marketCartCount > 0 && <b aria-hidden="true">{marketCartCount > 99 ? "99+" : marketCartCount}</b>}
            </Link>
            <Link className="et-header-publish" to="/mes-eternua/vendre/classique" aria-label={translateLabel("Publier une annonce")} title={translateLabel("Créer une annonce")}>
              <EternuaIcon name="publish" /><span>{translateLabel("Publier")}</span>
            </Link>
            <Link className="et-header-agenda" to={agendaDestination} aria-label={translateLabel("Ouvrir mon agenda")}
              title={translateLabel("Agenda")} aria-current={isAgendaRoute ? "page" : undefined}
              onClick={onPrimaryNavigate}>
              <EternuaIcon name="calendar" />
            </Link>
            <label className="et-global-language">
              <span className="sr-only">{translateLabel("Langue")}</span>
              <select value={lang} onChange={(event) => setLang(event.target.value)} aria-label={translateLabel("Choisir la langue")}>
                {langs.filter((item) => ["fr", "en"].includes(item.code)).map((item) => (
                  <option key={item.code} value={item.code}>{item.code.toUpperCase()}</option>
                ))}
              </select>
            </label>
            {notificationCenterEnabled && user ? (
              <NotificationBellPopover icon={<EternuaIcon name="bell" />} />
            ) : (
              <Link className="et-global-notifications" to="/notifications" aria-label={translateLabel("Notifications")}>
                <EternuaIcon name="bell" />
                {notificationCount > 0 ? <b>{notificationCount}</b> : null}
              </Link>
            )}
            {user ? (
              <div className="et-global-profile-menu" ref={profileMenuRef}>
                <button
                  type="button"
                  className={`et-global-profile${profileMenuOpen ? " is-open" : ""}`}
                  aria-label={translateMessage("navigation.profile_menu", `Ouvrir le menu de ${displayName}`, { name: displayName })}
                  aria-haspopup="menu"
                  aria-expanded={profileMenuOpen}
                  onClick={() => setProfileMenuOpen((open) => !open)}
                >
                  <MemberAvatar member={user} size="fill" alt={translateMessage("navigation.portrait", `Portrait de ${displayName}`, { name: displayName })} className="et-global-profile__avatar" context="global_navigation" />
                </button>
                {profileMenuOpen ? (
                  <div className="et-global-profile-dropdown" role="menu">
                    <div className="et-global-profile-dropdown__identity">
                      <span className="et-global-profile-dropdown__orb" aria-hidden="true">
                        <MemberAvatar member={user} size="fill" decorative className="et-global-profile__avatar" context="global_navigation_menu" />
                      </span>
                      <span>
                        <small>{translateLabel("Connecté en tant que")}</small>
                        <strong>{displayName}</strong>
                      </span>
                    </div>
                    <Link to="/profil" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                      <span aria-hidden="true">♙</span>
                      {translateLabel("Mon profil")}
                    </Link>
                    <Link to="/portefeuille" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                      <span aria-hidden="true">◈</span>
                      {translateLabel("Portefeuille")}
                    </Link>
                    <Link to="/mon-abonnement" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                      <span aria-hidden="true">✦</span>
                      {translate("subscriptions.nav.mine", "Mon abonnement")}
                    </Link>
                    <Link to="/mon-parcours" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                      <span aria-hidden="true">◇</span>
                      {translate("journey.nav", "Mon Parcours")}
                    </Link>
                    <Link to="/mes-eternua/recherche/enregistrees" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                      <span aria-hidden="true">⌕</span>
                      {translateLabel("Mes recherches et alertes")}
                    </Link>
                    <Link to="/mes-eternua/support" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                      <span aria-hidden="true">◌</span>
                      {translateLabel("Support & réclamations")}
                    </Link>
                    <Link to="/parrainage" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                      <span aria-hidden="true">♧</span>
                      {translateLabel("Parrainage")}
                    </Link>
                    {user?.access?.infini_public_beta_enabled ? (
                      <Link to="/mon-infini" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                        <span aria-hidden="true">∞</span>
                        {translate("infini.title", "Mon Infini")}
                      </Link>
                    ) : null}
                    <Link to="/studio-lia" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                      <span aria-hidden="true">✦</span>
                      {translate("studio.nav", "Studio LIA")}
                    </Link>
                    <button
                      type="button"
                      className="et-global-profile-dropdown__logout"
                      role="menuitem"
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                    >
                      <span aria-hidden="true">↪</span>
                      {translateLabel(isLoggingOut ? "Déconnexion…" : "Se déconnecter")}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <Link className="et-global-profile" to="/login" aria-label={translateLabel("Se connecter")}>
                <EternuaIcon name="people" />
              </Link>
            )}
          </div>
        </div>

        <nav className="et-header-navigation" aria-label={translateLabel("Navigation principale")}
          aria-hidden={secondaryNavHidden || undefined} inert={secondaryNavHidden}>
          <div className="et-header-navigation__links">
            {MAIN_NAVIGATION.filter(registryItemVisible).map((item) => (
              item.document ? <a key={item.to} href={item.to} onClick={() => setMoreOpen(false)}>
                <EternuaIcon name="spark" /><span>{itemLabel(item)}</span>
              </a> : <Link key={item.to} to={item.to} aria-current={isLinkActive(location, item) ? "page" : undefined}
                onClick={() => { setMoreOpen(false); onPrimaryNavigate?.(); if (item.panel) onCategoryNavigate?.(item.panel); }}>
                <EternuaIcon name={iconForPath(item.to)} /><span>{itemLabel(item)}</span>
              </Link>
            ))}
            <button type="button" ref={topMoreRef} aria-haspopup="dialog" aria-expanded={moreOpen === "top"}
              aria-controls="eternua-extra-links" onClick={(event) => toggleMore(event, "top")}><EternuaIcon name="more" /><span>{translateLabel("Plus")}</span></button>
          </div>
        </nav>
        {quickNavigation.available && <CollapsibleQuickNavigation
          contentId={quickNavigation.contentId}
          expanded={quickNavigation.expanded}
          hidden={secondaryNavHidden}
          onShortcutClick={quickNavigation.handleShortcutClick}
        >
          {isTempleHomepage ? (
            <nav
              className="et-global-context-actions et-global-context-actions--temple-home"
              aria-label={translateLabel("Centre de convergence Eternua")}
            >
              <div className="et-global-context-actions__rail">
              <SmartShortcut
                tone="today"
                icon="◉"
                label={translateLabel("Aujourd’hui")}
                subtitle={translateLabel("Usages disponibles")}
                to="/aujourdhui"
                current={false}
              />
              <SmartShortcut
                tone="vision"
                icon="✦"
                label={translateLabel("Vision 3024")}
                subtitle={translateLabel("Espace prospectif")}
                to="/vision-3024"
                current={false}
              />
              <RemainingMissionsButton
                className="et-global-context-shortcut et-global-context-shortcut--missions"
                linkClassName="et-global-context-shortcut__link"
                subtitle={translateLabel("Objectifs actifs et progression")}
              />
              <SmartShortcut
                tone="security"
                icon="◈"
                label={translateLabel("Sécurité")}
                subtitle={translateLabel("Centre de confiance")}
                to={protectionDestination}
                current={false}
              />
              <SmartShortcut
                tone="agenda"
                icon="▦"
                label={translateLabel("Voir mon agenda")}
                subtitle={translateLabel("Rendez-vous et repères")}
                to={agendaDestination}
                current={false}
              />
              <SmartShortcut
                tone="journey"
                icon="◇"
                label={translate("journey.nav", "Mon Parcours")}
                subtitle={translateLabel("Suivi de mes étapes")}
                to={journeyDestination}
                current={false}
              />
              <SmartShortcut
                tone="studio"
                icon="✦"
                label={translate("studio.nav", "Studio LIA")}
                subtitle={translateLabel("IA à venir · aucune IA active")}
                to={studioDestination}
                current={false}
              />
              </div>
            </nav>
          ) : (
            <nav
              className="et-global-context-actions et-global-context-actions--standard"
              aria-label={translateLabel("Raccourcis intelligents")}
            >
              <div className="et-global-context-actions__rail">
            <div
              className={`et-global-context-family et-global-context-family--priority${
                missionsShortcutHidden ? " et-global-context-family--without-missions" : ""
              }`}
              role="group"
              aria-labelledby="et-context-priority-label"
            >
              <span className="et-global-context-family__label" id="et-context-priority-label">
                {translateLabel("Priorité du jour")}
              </span>
              <div className="et-global-context-family__items">
                <SmartShortcut
                  tone="today"
                  icon="◉"
                  label={translateLabel("Aujourd’hui")}
                  subtitle={translateLabel("Mes priorités du moment")}
                  to="/aujourdhui"
                  current={location.pathname === "/aujourdhui"}
                />
                <RemainingMissionsButton
                  className="et-global-context-shortcut et-global-context-shortcut--missions"
                  linkClassName="et-global-context-shortcut__link"
                  subtitle={translateLabel("Objectifs actifs et progression")}
                  hideOnMissionsPage
                />
                <SmartShortcut
                  tone="vision"
                  icon="✦"
                  label={translateLabel("Vision 3024")}
                  subtitle={translateLabel("Explorer la trajectoire Eternua")}
                  to="/vision-3024"
                  current={location.pathname.startsWith("/vision-3024")}
                />
              </div>
            </div>

            <div
              className="et-global-context-family et-global-context-family--organisation"
              role="group"
              aria-labelledby="et-context-organisation-label"
            >
              <span className="et-global-context-family__label" id="et-context-organisation-label">
                {translateLabel("Organisation personnelle")}
              </span>
              <div className="et-global-context-family__items">
                <SmartShortcut
                  tone="agenda"
                  icon="▦"
                  label={translateLabel("Voir mon agenda")}
                  subtitle={translateLabel("Mes rendez-vous et repères")}
                  to={agendaDestination}
                  current={location.pathname.startsWith("/mes-eternua/agenda")}
                />
                <SmartShortcut
                  tone="journey"
                  icon="◇"
                  label={translate("journey.nav", "Mon Parcours")}
                  subtitle={translateLabel("Suivi de mes étapes")}
                  to={journeyDestination}
                  current={location.pathname.startsWith("/mon-parcours")}
                />
              </div>
            </div>

            <div
              className="et-global-context-family et-global-context-family--assistance"
              role="group"
              aria-labelledby="et-context-assistance-label"
            >
              <span className="et-global-context-family__label" id="et-context-assistance-label">
                {translateLabel("Protection et assistance")}
              </span>
              <div className="et-global-context-family__items">
                <SmartShortcut
                  tone="security"
                  icon="◈"
                  label={translateLabel("Sécurité")}
                  subtitle={translateLabel("Protection et centre de confiance")}
                  to={protectionDestination}
                  current={location.pathname.startsWith("/mes-eternua/protection")}
                />
                <SmartShortcut
                  tone="studio"
                  icon="✦"
                  label={translate("studio.nav", "Studio LIA")}
                  subtitle={translateLabel("Outils et accompagnement futur")}
                  to={studioDestination}
                  current={location.pathname.startsWith("/studio-lia")}
                />
              </div>
            </div>
              </div>
            </nav>
          )}
        </CollapsibleQuickNavigation>}
      </header>

      {emailVerificationRequired ? (
        <nav
          className="et-global-email-reminder"
          aria-label={translateLabel("Confirmation de l’adresse e-mail")}
        >
          <span className="et-global-email-reminder__icon" aria-hidden="true">@</span>
          <span className="et-global-email-reminder__message" role="status">
            <strong>{translateLabel("E-mail non confirmé")}</strong>
            <small>{translateLabel("Certaines fonctions restent bloquées tant que votre adresse n’est pas vérifiée.")}</small>
          </span>
          <Link
            className="et-global-email-reminder__action"
            to={emailVerificationDestination}
            onClick={() => setMoreOpen(false)}
            aria-label={translateLabel("Confirmer mon adresse e-mail")}
          >
            <span className="et-global-email-reminder__action-full">{translateLabel("Confirmer mon e-mail")}</span>
            <span className="et-global-email-reminder__action-short" aria-hidden="true">{translateLabel("Confirmer")}</span>
          </Link>
        </nav>
      ) : null}

      <nav className="et-global-dock et-global-dock--more" aria-label={translateLabel("Navigation sociale")}>
        <button
          type="button"
          className={`et-global-dock__brand${messengerOpen ? " is-open" : ""}`}
          onClick={() => {
            if (!user) {
              navigate("/login");
              return;
            }
                  setMessengerOpen((open) => !open);
          }}
          aria-label={
            unreadMessageCount > 0
              ? translateMessage(
                unreadMessageCount === 1 ? "navigation.messages_unread_one" : "navigation.messages_unread_many",
                `Ouvrir la messagerie, ${unreadMessageCount} message${unreadMessageCount > 1 ? "s" : ""} non lu${unreadMessageCount > 1 ? "s" : ""}`,
                { count: unreadMessageCount }
              )
              : translateLabel("Ouvrir la messagerie")
          }
          aria-expanded={messengerOpen}
          aria-controls="eternua-mini-messenger"
          title={translateLabel("Messagerie")}
        >
          <span className="et-global-orb et-global-orb--dock" aria-hidden="true">
            <i />
            <svg
              className="et-global-dock__message-icon"
              viewBox="0 0 24 24"
              focusable="false"
            >
              <path d="M6.2 5.8h11.6a2.7 2.7 0 0 1 2.7 2.7v6.1a2.7 2.7 0 0 1-2.7 2.7h-6.5l-4.8 3v-3h-.3a2.7 2.7 0 0 1-2.7-2.7V8.5a2.7 2.7 0 0 1 2.7-2.7Z" />
              <path d="M7.7 10h8.6M7.7 13.1h5.7" />
            </svg>
          </span>
          {user && unreadMessageCount > 0 ? (
            <span className="et-global-dock__message-count" aria-hidden="true">
              {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
            </span>
          ) : null}
        </button>
        <div className="et-global-dock__links">
          {DOCK_LINKS.filter(registryItemVisible).map((item) => {
            const active = isLinkActive(location, item);
            const label = itemLabel(item);
            return (
              <Link
                key={item.label}
                to={item.to}
                className={active ? "is-active" : ""}
                aria-current={active ? "page" : undefined}
                aria-label={translateLabel(item.accessibleLabel)}
                title={translateLabel(item.accessibleLabel)}
                onClick={(event) => openDockDestination(event, item.to)}
              >
                <EternuaIcon name={iconForPath(item.to)} />
                <strong>{label}</strong>
              </Link>
            );
          })}
        </div>
          <button type="button" className="et-global-dock__more" ref={bottomMoreRef}
            aria-haspopup="dialog" aria-expanded={moreOpen === "bottom"} aria-controls="eternua-extra-links"
            onClick={(event) => toggleMore(event, "bottom")}>
            <EternuaIcon name="more" /><strong>{translateLabel("Plus")}</strong>
          </button>
        <WalletControl />
      </nav>
      {moreOpen ? (
        <div id="eternua-extra-links" ref={moreRef} className={`et-global-extra-links et-global-extra-links--${moreOpen}`}
          role="dialog" aria-label={translateLabel("Plus de liens")}>
          <div className="et-global-extra-links__heading">
            <div className="et-global-extra-links__intro">
              <span className="et-global-extra-links__eyebrow">{translateLabel(moreOpen === "top" ? "Explorer Eternua" : "Votre espace Eternua")}</span>
              <strong>{translateLabel(moreOpen === "top" ? "Aujourd’hui et découvertes" : "Vie sociale et espace personnel")}</strong>
              <p>{translateLabel(moreOpen === "top" ? "Des services, des idées et de nouvelles possibilités." : "Vos liens, vos échanges et ce qui compte pour vous.")}</p>
            </div>
            <button type="button" aria-label={translateLabel("Fermer Plus")} onClick={() => {
              setMoreOpen(false);
              moreTriggerRef.current?.focus();
            }}>×</button>
          </div>
          <nav aria-label={translateLabel(moreOpen === "top" ? "Aujourd’hui et projet Eternua" : "Vie sociale et espace personnel")}>
            <Link className="et-global-extra-links__search" to="/recherche" onClick={() => setMoreOpen(false)}>
              <EternuaIcon name="search" /><span>{translateLabel("Rechercher sur tout Eternua")}</span>
            </Link>
            {moreOpen === "top" ? <>
            {topTodayFeatures.length > 0 ? <section aria-label={translateLabel("Toutes les rubriques d’Aujourd’hui")}>
              <h2>{translateLabel("Les rubriques d’Aujourd’hui")}</h2>
              <div className="et-global-extra-links__categories">
                {groupTodayMenu(topTodayFeatures).map((section) => (
                  <details className="et-global-extra-links__category" key={section.label} open={!compactMenu}>
                    <summary>
                      <EternuaIcon name={section.icon} />
                      <strong>{translateLabel(section.label)}</strong>
                      <span className="et-global-extra-links__category-count">{section.features.length}</span>
                      <span className="et-global-extra-links__category-chevron" aria-hidden="true">⌄</span>
                    </summary>
                    <div className="et-global-extra-links__grid">{renderTodayLinks(section.features)}</div>
                  </details>
                ))}
              </div>
            </section> : null}
            <section aria-label={translateLabel("Services et découvertes")}>
              <h2>{translateLabel("Services et découvertes")}</h2>
              <div className="et-global-extra-links__grid">{renderMoreLinks([
                ...ACCOUNT_LINKS, ...CATEGORY_LINKS,
                { icon: "✧", label: "Entraide", to: "/entraide", featureKey: "today-mutual-aid" },
                { label: "Historique des notifications", to: "/notifications", accessibleLabel: notificationCount > 0 ? translateMessage("navigation.notifications_history_count", `Messagerie et notifications historiques, ${notificationCount} non lues`, { count: notificationCount }) : translateLabel("Messagerie et notifications historiques") },
                { icon: "☀", label: "Météo", to: "/meteo" },
                { icon: "△", label: "Temples", to: "/temples" },
                { icon: "▤", label: "Forum", to: "/forum", featureKey: "today-forum" },
                { icon: "🛒", label: "Panier", to: "/market/panier", accessibleLabel: marketCartCount ? translateMessage("navigation.cart_count", `Ouvrir mon panier, ${marketCartCount} articles`, { count: marketCartCount }) : translateLabel("Ouvrir mon panier, panier vide") },
              ].filter(absentFromCatalog(topTodayFeatures)))}</div>
            </section>
            <section aria-label={translateLabel("Projet Eternua")}>
              <h2>{translateLabel("Projet Eternua")}</h2>
              <div className="et-global-extra-links__grid">{renderMoreLinks(MORE_PROJECT_LINKS.filter(absentFromCatalog(topTodayFeatures)))}</div>
            </section>
            </> : (
            <section aria-label={translateLabel("Vie sociale et espace personnel")}>
              <h2>{translateLabel("Vie sociale et espace personnel")}</h2>
              <div className="et-global-extra-links__grid">
                <Link to={user ? "/?messenger=1&view=full" : buildGuestLoginDestination("/?messenger=1&view=full")}
                  onClick={(event) => {
                    setMoreOpen(false);
                    if (user) {
                      event.preventDefault();
                      setMessengerExpanded(true);
                      setMessengerOpen(true);
                    }
                  }}>
                  <EternuaIcon name="message" />
                  <span className="et-global-extra-links__copy"><strong>{translateLabel("Messagerie")}</strong><small>{translateLabel("Vos conversations en grand")}</small></span>
                </Link>
                {renderTodayLinks(socialTodayFeatures.filter((feature) => feature.featureKey !== "today-chat"))}
                {renderMoreLinks([
                  ...SOCIAL_LINKS.filter((item) => item.to !== "/amis"),
                  { icon: "◇", label: "Mes Eternua", to: "/mes-eternua" },
                ].filter(absentFromCatalog(socialTodayFeatures)))}
              </div>
            </section>
            )}
          </nav>
        </div>
      ) : null}
      <FriendsDock
        messengerOnly
        messengerOpen={messengerOpen}
        messengerExpanded={messengerExpanded}
        onMessengerExpandedChange={setMessengerExpanded}
        onMessengerOpenChange={setMessengerOpen}
      />
    </>
  );
}
